
import React, { useState, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Box, Container, Grid, Typography, Link, IconButton, Stack, Divider, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FavoriteIcon from '@mui/icons-material/Favorite';

export default function Footer() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [showCreator, setShowCreator] = useState(false);
  const timerRef = useRef(null);

  const handleMouseDown = () => {
    timerRef.current = setTimeout(() => {
      setShowCreator(true);
    }, 2000); // reduced to 2s for better UX
  };

  const handleMouseUp = () => {
    clearTimeout(timerRef.current);
    // Don't hide immediately to let them click the link if it appeared
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setShowCreator(false);
  };

  const handleNav = (path) => {
    navigate(path, { state: { resetScroll: true } });
    window.scrollTo(0, 0);
    const scrollContainer = document.getElementById('scroll-container');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#fcfdfa',
        pt: 10,
        pb: 4,
        borderTop: '1px solid',
        borderColor: 'rgba(0,0,0,0.05)',
        mt: 'auto',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Decorative Top Gradient Line */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #75a84b 0%, #a3d977 50%, #75a84b 100%)', opacity: 0.8 }} />

      <Container maxWidth="lg">
        <Grid container spacing={5}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Box component={RouterLink} to="/" state={{ intentional: true, resetScroll: true }} onClick={() => {
              window.scrollTo(0, 0);
              const scrollContainer = document.getElementById('scroll-container');
              if (scrollContainer) scrollContainer.scrollTop = 0;
            }} sx={{ display: 'flex', alignItems: 'center', gap: 2, textDecoration: 'none' }}>
              <Box
                component="img"
                src="/logooo2.png"
                alt="Matax Express Logo"
                sx={{ height: '60px' }}
              />
              <Box sx={{ ml: -1.5 }}>
                <Typography variant="body1" sx={{ color: 'black', fontWeight: 'bold', lineHeight: 1, fontSize: '1.5rem', fontStyle: 'italic' }}>
                  Matax
                </Typography>
                <Typography variant="body1" sx={{ color: 'black', fontWeight: 'bold', lineHeight: 1, fontSize: '1.3rem', fontStyle: 'italic' }}>
                  Express
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 3, maxWidth: '300px', lineHeight: 1.7 }}>
              Your trusted partner for premium wholesale produce. Delivering freshness, quality, and reliability to businesses 7 days a week.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <IconButton
                size="small"
                href="https://instagram.com" target="_blank"
                sx={{ bgcolor: 'white', border: '1px solid #eee', color: '#E1306C', transition: 'all 0.2s', '&:hover': { bgcolor: '#E1306C', color: 'white', borderColor: '#E1306C', transform: 'translateY(-3px)' } }}
              >
                <InstagramIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                href="https://facebook.com" target="_blank"
                sx={{ bgcolor: 'white', border: '1px solid #eee', color: '#1877F2', transition: 'all 0.2s', '&:hover': { bgcolor: '#1877F2', color: 'white', borderColor: '#1877F2', transform: 'translateY(-3px)' } }}
              >
                <FacebookIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>

          {/* Quick Links (Mini Menu) */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2.5, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Explore</Typography>
            <Stack spacing={2}>
              {['Browse Catalog', 'About Us', 'Contact Us'].map((text, index) => (
                <Link
                  key={text}
                  component="button"
                  onClick={() => handleNav(index === 0 ? '/browse' : index === 1 ? '/about' : '/care')}
                  underline="none"
                  color="text.secondary"
                  sx={{
                    textAlign: 'left',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#75a84b', transform: 'translateX(5px)' }
                  }}
                >
                  {text}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Support Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2.5, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Support</Typography>
            <Stack spacing={2}>
              {['Why Shop With Us', 'FAQs'].map((text, index) => (
                <Link
                  key={text}
                  component="button"
                  onClick={() => handleNav(index === 0 ? '/why-us' : index === 1 ? '/faq' : '/privacy')}
                  underline="none"
                  color="text.secondary"
                  sx={{
                    textAlign: 'left',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#75a84b', transform: 'translateX(5px)' }
                  }}
                >
                  {text}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={4} md={4}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2.5, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Contact Us</Typography>
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Link
                  href="https://www.google.com/maps/place/Matax+Express+Ltd/data=!4m2!3m1!1s0x0:0x5a4cdf2c0519500c?sa=X&ved=1t:2428&ictx=111"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                >
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(117,168,75,0.1)', color: '#75a84b' }}>
                    <LocationOnIcon fontSize="small" />
                  </Box>
                </Link>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  <Link
                    href="https://www.google.com/maps/place/Matax+Express+Ltd/data=!4m2!3m1!1s0x0:0x5a4cdf2c0519500c?sa=X&ved=1t:2428&ictx=111"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="hover"
                  >
                    Matax Express Ltd<br />
                    415 Oakdale Rd<br />
                    Toronto, ON M3N 1W7<br />
                    Canada
                  </Link>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Link href="tel:+14169134727" underline="none">
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(117,168,75,0.1)', color: '#75a84b' }}>
                    <PhoneIcon fontSize="small" />
                  </Box>
                </Link>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  <Link href="tel:+14169134727" color="inherit" underline="hover">
                    +1 (416) 913-4727
                  </Link>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Link href="mailto:info@mataxexpress.ca" underline="none">
                  <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(117,168,75,0.1)', color: '#75a84b' }}>
                    <EmailIcon fontSize="small" />
                  </Box>
                </Link>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  <Link href="mailto:info@mataxexpress.ca" color="inherit" underline="hover">
                    info@mataxexpress.ca
                  </Link>
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5, borderColor: 'rgba(0,0,0,0.08)' }} />

        {/* Bottom Bar */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            © {new Date().getFullYear()} Matax Express Ltd. All rights reserved.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* <Typography variant="caption" color="text.disabled" sx={{ letterSpacing: '0.5px' }}>
              DESIGNED WITH
              <FavoriteIcon sx={{ fontSize: 10, color: '#ff6b6b', mx: 0.5, verticalAlign: 'middle' }} />
              BY
            </Typography> */}
            {/* Easter Egg / Credit */}
            {/* <Box
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onClick={() => setShowCreator(!showCreator)}
              sx={{ display: 'inline-flex', cursor: 'pointer', position: 'relative' }}
            >
              <Link
                href={showCreator ? "https://www.linkedin.com/in/vaibhav-arora-913938212/" : "#"}
                target="_blank"
                underline="none"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  color: showCreator ? '#75a84b' : 'text.disabled',
                  transition: 'color 0.3s'
                }}
              >
                {showCreator ? 'VAIBHAV ARORA' : 'VA'}
              </Link>
            </Box> */}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}